import type { SajuAnalysis } from '../types/input';
import type { Ohaeng } from '../types/elements';
import type { OhaengHarmony } from '../types/compatibility';
import { getStemElement, getElementRelationship } from '../core/five-elements';

const OHAENG_LIST: Ohaeng[] = ['木', '火', '土', '金', '水'];

const RELATION_LABEL: Record<OhaengHarmony['dayMasterRelation'], string> = {
  same: '비화(比和)',
  generates: '상생(생)',
  generated: '상생(받음)',
  overcomes: '상극(극)',
  overcome: '상극(받음)',
};

/**
 * 두 사주의 오행 조화를 분석한다.
 *
 * 1. 일간 오행 간 상생상극 판정
 * 2. 오행 분포 보완도: 한쪽이 약한 오행을 상대가 보충하는 정도
 */
export function analyzeOhaengHarmony(
  analysis1: SajuAnalysis,
  analysis2: SajuAnalysis,
): OhaengHarmony {
  const elem1 = getStemElement(analysis1.dayMaster);
  const elem2 = getStemElement(analysis2.dayMaster);
  const relation = getElementRelationship(elem1, elem2);

  const dist1 = analysis1.ohaengDistribution;
  const dist2 = analysis2.ohaengDistribution;

  // 보완도 계산: 한쪽이 약한 오행을 상대가 보충하는 비율
  // ohaengDistribution 값은 절대값(합계 ~12)이므로 평균(2.4)의 절반을 약함 기준으로 사용
  const total1 = OHAENG_LIST.reduce((s, o) => s + dist1[o], 0);
  const total2 = OHAENG_LIST.reduce((s, o) => s + dist2[o], 0);
  const weakThreshold1 = (total1 / 5) * 0.5;
  const weakThreshold2 = (total2 / 5) * 0.5;
  const strongThreshold1 = (total1 / 5) * 0.8;
  const strongThreshold2 = (total2 / 5) * 0.8;

  const complementary: Ohaeng[] = [];
  for (const ohaeng of OHAENG_LIST) {
    if ((dist1[ohaeng] < weakThreshold1 && dist2[ohaeng] > strongThreshold2) ||
        (dist2[ohaeng] < weakThreshold2 && dist1[ohaeng] > strongThreshold1)) {
      complementary.push(ohaeng);
    }
  }
  const distributionBalance = complementary.length / 5;

  const description = `일간 ${elem1}↔${elem2} ${RELATION_LABEL[relation]}, 오행 보완도 ${(distributionBalance * 100).toFixed(0)}%` +
    (complementary.length > 0 ? ` (보완: ${complementary.join(',')})` : '');

  return {
    dayMasterRelation: relation,
    dayMasterElements: [elem1, elem2],
    distributionBalance,
    complementaryElements: complementary,
    description,
  };
}
